import React, { useState, useRef, useEffect } from 'react';
import { default as Select, components } from 'react-select';
import { format } from 'date-fns';
import DateRange from '../DateRange/DateRange';
import { DropdownIndicator } from '../CustomDropdownIndicator/CustomDropdownIndicator';
import { DateFilterTypes, getDateFilterOptionsFromValues } from '../../config/FilterOptionTypes';

import './DateFilter.css';
import CustomDatePicker from '../CustomDatePicker/CustomDatePicker';

// Declare custom components outside of Select render to prevent unexpected behavior
const Control = ({ children, ...props }) => {
    props.innerProps.onClick = () => props.selectProps.customProps.toggleMenuIsOpen()
    let { dateFilterLabel, isMobile } = props.selectProps.customProps;
    return (<components.Control {...props}>
        {isMobile?<></>:<div className="efup-filter-label cb-font-poppins efup-date-filter-label" data-testid={`efup-date-filter-display-label`}>{dateFilterLabel ? dateFilterLabel : "Date:"}</div>}
         {children}
    </components.Control>);
};

const SingleValue = ({ children, ...props }) => {
    // Hide other options if all is selected
    const selectedValue = props.selectProps.value;
    if (selectedValue.value === 'custom-date-range') {
        let { startDate, endDate } = props.selectProps.customProps;
        return (
            <components.SingleValue {...props}>
                <div className="cb-font-poppins efup-date-filter-option-display" data-testid={`efup-date-filter-display-selected-value`}>{startDate ? format(startDate, 'MM/dd/yyyy') : "mm/dd/yyyy"}{" - "}{endDate ? format(endDate, 'MM/dd/yyyy') : "mm/dd/yyyy"}</div>
            </components.SingleValue>
        );
    }
    return (
        <components.SingleValue {...props}>
            <div className="cb-font-poppins efup-date-filter-option-display" data-testid={`efup-date-filter-display-selected-value`}>{selectedValue.label}</div>
        </components.SingleValue>
    );
};

const Option = (props) => {
    let { startDate, setStartDate, endDate, setEndDate, toggleMenuIsOpen, setEndCalendarOpen, setStartCalendarOpen } = props.selectProps.customProps;

    if (props.value === 'custom-date-range') {
        props.innerProps.onClick = () => {
            props.setValue({ label: props.label, value: props.value });
        }
        return (
            <components.Option {...props} className={`efup-filter-option efup-date-dropdown-option`}>
                <label data-testid={`efup-date-filter-option-${props.value}`}>{props.label}</label>
                {props.isSelected ? <DateRange
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                    customClasses="efup-custom-date-range-dropdown"
                    setStartCalendarOpen={setStartCalendarOpen}
                    setEndCalendarOpen={setEndCalendarOpen}
                /> : <React.Fragment />}

            </components.Option>
        );
    }
    props.innerProps.onClick = () => {
        // Clear out Custom Dates when selecting a non Custom Range value
        setStartDate(undefined);
        setEndDate(undefined);
        toggleMenuIsOpen();
        props.setValue({ label: props.label, value: props.value });
    }
    return (
        <components.Option {...props} className={`efup-filter-option efup-date-dropdown-option`} >
            <label data-testid={`efup-date-filter-option-${props.value}`}>{props.label}</label>
        </components.Option>
    );
};


export default function DateFilter(props) {
    const dateFilterOptions = props.options;
    const dateFilterLabel = props.filterLabel;
    const isMobile = props.isMobile;
    const dateFilterDefault = props.defaultValue ?? DateFilterTypes[3];
    // Default to Last 12 Months selected
    const [selectedOption, setSelectedOption] = useState(dateFilterDefault);
    const [startDate, setStartDate] = useState(props.startDate);
    const [endDate, setEndDate] = useState(props.endDate);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [startCalendarOpen, setStartCalendarOpen] = useState(false);
    const [endCalendarOpen, setEndCalendarOpen] = useState(false);

    function handleListChange(event) {
        setSelectedOption(event);
        if (props.onChange) {
            props.onChange(event.value, startDate, endDate);
        }
    }

    useEffect(() => {
        if (startDate && endDate) {
            if (selectedOption.value === 'custom-date-range') {
                if (!(props.lastDateFilterType === 'custom-date-range' && (startDate === props.startDate) && (endDate === props.endDate))) {
                    props.setParentDates(startDate, endDate)
                }               
            }    

            if (dateFilterOptions === DateFilterTypes) {
                // Auto-Selection for DateFilterTypes
                let option = getDateFilterOptionsFromValues(startDate, endDate);
                setSelectedOption(option);
                props.setDisable(false) //disable apply button in parent on payments page
            }
        } else {
            if (dateFilterOptions === DateFilterTypes) {
                props.setDisable(true)
            }
        }
    }, [startDate, endDate]);     // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
            setStartDate(props.startDate);
            setEndDate(props.endDate);

    }, [props.startDate, props.endDate]);

    useEffect(() => {
        if (startCalendarOpen || endCalendarOpen) {
            setIsMenuOpen(false);
        }
    }, [startCalendarOpen, endCalendarOpen]);

    function toggleMenuIsOpen() {
        if (isMenuOpen) {
            setIsMenuOpen(false);
        } else {
            setIsMenuOpen(true);
        }
    }

    function handleKeyDown(e) {
        if (e.key === "Escape") {
            setIsMenuOpen(false);
        }
        if (e.key === "Enter") {
            toggleMenuIsOpen();
        }
    }

    function handleCalendarOnBlur() {
        setStartCalendarOpen(false);
        setEndCalendarOpen(false);
        dateFilterRef.current.focus();
    }

    function handleOnBlur() {
        setIsMenuOpen(false);
    }

    const dateFilterRef = useRef(null);

    function handleDateSelection(date) {
        if (startCalendarOpen) {
            setStartDate(date);
            if (props.onChange) {
                props.onChange(selectedOption.value, date, endDate);
            }
            setStartCalendarOpen(false);
            if (!(startDate && endDate)) {
                handleFilterFocus();
            }
        } else {
            setEndDate(date);
            setEndCalendarOpen(false);
            if (props.onChange) {
                props.onChange(selectedOption.value, startDate, date);
            }
            if (!(startDate && endDate)) {
                handleFilterFocus();
            }
        }
    }

    function handleCalendarSelection(selection) {
        if (startCalendarOpen) {
            setStartCalendarOpen(selection);
            if (!(startDate && endDate) && !selection) {
                handleFilterFocus();
            }
        } else {
            setEndCalendarOpen(selection);
            if (!(startDate && endDate) && !selection) {
                handleFilterFocus();
            }
        }
    }

    function handleFilterFocus() {
        dateFilterRef.current.focus();
        setIsMenuOpen(true);
    }

    const customStyles = {
        menu: (provided) => ({
            ...provided,
            width: 'max-content',
            height: 'max-content',
            minWidth: '100%',
            marginTop: '5px'
        }),
        menuList: (provided) => ({
            ...provided,
            overflowY: 'visible'
        }),
        valueContainer: (provided) => ({
            ...provided,
            paddingRight: 0
        }),
        control: (styles, { isDisabled, isFocused, isSelected }) => {
            return {
                ...styles,
                boxShadow: isFocused ? '0 0 0 1px #00b5e2' : undefined,
                borderColor: isDisabled
                    ? undefined
                    : isSelected && !isFocused
                        ? '#00b5e2'
                        : isFocused
                            ? '#00b5e2'
                            : '#D3D6DE',
                ':hover': {
                    borderColor: '#00b5e2',
                }
            }
        },
        option: (styles, { isDisabled, isFocused, isSelected }) => {
            return {
                ...styles,
                backgroundColor: isDisabled
                    ? undefined
                    : isSelected && !isFocused
                        ? 'transparent'
                        : isFocused
                            ? '#00b5e2'
                            : undefined,
                cursor: isDisabled ? 'not-allowed' : 'default',

                ':active': {
                    ...styles[':active'],
                    backgroundColor: !isDisabled
                        ? isSelected
                            ? '#00b5e2'
                            : '#00b5e2'
                        : undefined,
                },
                ':hover': {
                    backgroundColor: !isDisabled
                        ? isSelected
                            ? '#00b5e2'
                            : '#00b5e2'
                        : undefined,
                }
            };
        },
    }

    return (
        <div className="efup-date-filter-wrapper">
            <Select
                placeholder={`Please Select a Date Range`}
                className="cb-date-select cb-form efup-date-filter-select"
                components={{
                    IndicatorSeparator: () => null,
                    Option,
                    SingleValue,
                    Control,
                    DropdownIndicator
                }}
                ref={dateFilterRef}
                options={dateFilterOptions}
                defaultValue={dateFilterDefault}
                isSearchable={false}
                customProps={{
                    startDate,
                    setStartDate,
                    endDate,
                    setEndDate,
                    toggleMenuIsOpen,
                    setStartCalendarOpen,
                    setEndCalendarOpen,
                    selectType: 'date-filter',
                    dateFilterLabel,
                    isMobile
                }}
                hideSelectedOptions={false}
                value={selectedOption}
                styles={customStyles}
                closeMenuOnSelect={false}
                onChange={handleListChange}
                menuIsOpen={isMenuOpen}
                onBlur={handleOnBlur}
                onKeyDown={handleKeyDown}
            />
            {startCalendarOpen || endCalendarOpen ?
                <CustomDatePicker
                    selectedDate={startCalendarOpen ? startDate : endDate}
                    setDate={handleDateSelection}
                    selectsStart={startCalendarOpen ? true : false}
                    selectsEnd={endCalendarOpen ? true : false}
                    startDate={startDate}
                    endDate={endDate}
                    hidePopperArrow
                    onBlur={handleCalendarOnBlur}
                    isOpen={startCalendarOpen || endCalendarOpen}
                    customClasses={"efup-date-filter-date-dropdown"}
                    setCalendarOpen={handleCalendarSelection}
                    type={startCalendarOpen ? 'range-start' : 'range-end'}
                    shouldDisplayClose
                /> : <React.Fragment />}
        </div>
    );
}